import { v4 as uuid } from "uuid";
import { isProblem, type Problem } from "./problem";

// `next/headers` is server-only; import dynamically inside ssrFetch so this
// module can also be imported from Client Components (which call clientApi).
// P05-T7 was the first Client Component consumer; before that the file lived
// happily in Server-only land.

const BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:4000";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly problem: Problem | null,
  ) {
    super(message);
    this.name = "ApiError";
  }

  static async fromResponse(res: Response): Promise<ApiError> {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // non-JSON body
    }
    if (isProblem(body)) {
      return new ApiError(body.title, res.status, body);
    }
    return new ApiError(`HTTP ${res.status}`, res.status, null);
  }
}

/**
 * Server-side fetch from a Server Component. Forwards inbound request cookies
 * so the api's auth.middleware sees the session.
 *
 * Uses cache: "no-store" by default — price-bearing endpoints are personalised.
 * Override with { revalidate: N } for cacheable reads (categories tree).
 *
 * P06-T11 extended this helper to support server-side mutations (e.g. the
 * /checkout RSC POSTs to /v1/storefront/checkout with a server-generated
 * Idempotency-Key). When `method` is set to a non-GET verb the helper
 * forwards `headers` and a JSON-serialised `body` and disables Next caching
 * regardless of `revalidate`.
 */
export interface SsrFetchOptions {
  revalidate?: number;
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: string;
}

export async function ssrFetch<T>(
  path: string,
  opts: SsrFetchOptions = {},
): Promise<T> {
  const { cookies } = await import("next/headers");
  const jar = await cookies();
  const cookie = jar.toString();
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (cookie) headers["cookie"] = cookie;
  const init: RequestInit & { next?: { revalidate?: number } } = {
    method,
    headers,
  };
  if (opts.body !== undefined) init.body = opts.body;
  if (method === "GET" && opts.revalidate !== undefined) {
    init.next = { revalidate: opts.revalidate };
  } else {
    init.cache = "no-store";
  }
  const res = await fetch(`${BASE}${path}`, init);
  if (!res.ok) throw await ApiError.fromResponse(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Appends ?locale= (or &locale=) to a storefront catalog path. Keeping locale
 * in the URL — not a header — is deliberate: Next ISR keys on the URL string,
 * so each locale gets its own cache entry automatically.
 */
export function withLocale(path: string, locale: string): string {
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}locale=${encodeURIComponent(locale)}`;
}

/**
 * Client-side fetch (for use in Client Components: sign-in form, sign-up form,
 * etc.). Goes through the Next rewrite at /api/auth/* so cookies stay same-origin.
 *
 * Adds idempotency-key on mutations per spec §7.4.
 */
interface ClientRequestOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  formData?: FormData;
  idempotencyKey?: string;
  /** Optimistic-concurrency guard — forwarded as the `If-Match` request header. */
  ifMatch?: string;
}

async function clientRequest<T>(
  path: string,
  opts: ClientRequestOpts = {},
): Promise<T> {
  const method = opts.method ?? "GET";
  if (opts.body !== undefined && opts.formData !== undefined) {
    throw new Error("clientRequest cannot send both JSON and FormData bodies");
  }
  const headers: Record<string, string> = {
    accept: "application/json",
  };
  if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
  }
  if (method === "POST" || method === "PUT" || method === "PATCH" || method === "DELETE") {
    headers["idempotency-key"] = opts.idempotencyKey ?? uuid();
  }
  if (opts.ifMatch !== undefined) {
    headers["if-match"] = opts.ifMatch;
  }
  const init: RequestInit = {
    method,
    credentials: "include",
    headers,
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.formData !== undefined) init.body = opts.formData;
  const res = await fetch(path, init);
  if (!res.ok) throw await ApiError.fromResponse(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const clientApi = {
  get: <T>(path: string) => clientRequest<T>(path),
  post: <T>(path: string, body: unknown, opts?: ClientRequestOpts) =>
    clientRequest<T>(path, { method: "POST", body, ...opts }),
  postForm: <T>(
    path: string,
    formData: FormData,
    opts?: Omit<ClientRequestOpts, "method" | "body" | "formData">,
  ) => clientRequest<T>(path, { method: "POST", formData, ...opts }),
  patch: <T>(path: string, body: unknown, opts?: ClientRequestOpts) =>
    clientRequest<T>(path, { method: "PATCH", body, ...opts }),
  put: <T>(path: string, body: unknown, opts?: ClientRequestOpts) =>
    clientRequest<T>(path, { method: "PUT", body, ...opts }),
  del: <T>(path: string, opts?: ClientRequestOpts) =>
    clientRequest<T>(path, { method: "DELETE", ...opts }),
};
